import { useEffect, useRef, useState } from "react";
import Lenis from "lenis";
import { motion, useScroll, useTransform } from "framer-motion";
import { Calendar, CalendarPlus, MapPin, MessageCircle } from "lucide-react";
import Countdown from "@/components/Countdown";
import Marquee from "@/components/Marquee";
import Chapter from "@/components/Chapter";
import Gallery from "@/components/Gallery";
import HoofTracks from "@/components/HoofTracks";
import ErasedHoof from "@/components/ErasedHoof";
import {
  Shield,
  Calabash,
  Basket,
  CowHead,
  GiftBox,
  Leaves,
  MonogramPlaque,
  ZuluAttire,
} from "@/components/Decor";

const EASE = [0.76, 0, 0.24, 1];

const MaskedLine = ({ index, revealed, className, children }) => (
  <span className="block overflow-hidden">
    <motion.span
      className={`block ${className}`}
      initial={{ y: "115%" }}
      animate={revealed ? { y: "0%" } : {}}
      transition={{ duration: 1.05, ease: EASE, delay: 0.35 + index * 0.15 }}
    >
      {children}
    </motion.span>
  </span>
);

const FadeUp = ({ revealed, delay, className, children }) => (
  <motion.div
    initial={{ opacity: 0, y: 22 }}
    animate={revealed ? { opacity: 1, y: 0 } : {}}
    transition={{ delay, duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
    className={className}
  >
    {children}
  </motion.div>
);

const RSVP_MESSAGE = encodeURIComponent(
  "Sawubona! I would like to RSVP for the Umbondo / Ingqibamasondo ka MaMdineka on 26 September 2026.",
);

const RSVP_CONTACTS = [
  {
    label: "In Support of uMaMdineka",
    display: "076 643 1495",
    url: `https://wa.me/27766431495?text=${RSVP_MESSAGE}`,
    testid: "rsvp-mamdineka-link",
  },
  {
    label: "In Support of uMfanelo",
    display: "078 277 2370",
    url: `https://wa.me/27782772370?text=${RSVP_MESSAGE}`,
    testid: "rsvp-umfanelo-link",
  },
];

const MAPS_URL = "https://maps.app.goo.gl/ZcySH8YiFxxXFoFe8";

export default function Invitation({ revealed }) {
  const heroRef = useRef(null);
  const [hasRsvpd, setHasRsvpd] = useState(
    () => localStorage.getItem("mbele-rsvpd") === "1",
  );
  const markRsvpd = () => {
    localStorage.setItem("mbele-rsvpd", "1");
    setHasRsvpd(true);
  };

  useEffect(() => {
    if (!revealed) return undefined;
    const lenis = new Lenis({ duration: 1.15, smoothWheel: true });
    let raf;
    const loop = (t) => {
      lenis.raf(t);
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => {
      cancelAnimationFrame(raf);
      lenis.destroy();
    };
  }, [revealed]);

  const { scrollYProgress } = useScroll({
    target: heroRef,
    offset: ["start start", "end start"],
  });
  const yShield = useTransform(scrollYProgress, [0, 1], [0, 130]);
  const yCow = useTransform(scrollYProgress, [0, 1], [0, -100]);
  const heroOpacity = useTransform(scrollYProgress, [0, 0.85], [1, 0]);

  return (
    <main
      data-testid="invitation"
      className="relative overflow-x-clip bg-[#FAF8F5] text-[#4A3B32]"
    >
      <HoofTracks />
      {/* HERO */}
      <section
        ref={heroRef}
        className="relative flex min-h-svh flex-col items-center justify-center overflow-hidden px-6 py-24"
      >
        <motion.div
          style={{ y: yShield }}
          className="pointer-events-none absolute -left-10 top-16 text-[#C5A059]/25 sm:left-8"
          aria-hidden="true"
        >
          <Shield className="h-44 w-28 sm:h-64 sm:w-40" />
        </motion.div>
        <motion.div
          style={{ y: yCow }}
          className="pointer-events-none absolute -right-8 bottom-24 text-[#5A3A5A]/20 sm:right-10"
          aria-hidden="true"
        >
          <CowHead className="h-32 w-36 sm:h-44 sm:w-52" />
        </motion.div>
        <div
          className="pointer-events-none absolute inset-x-0 top-0 bead-band opacity-60"
          aria-hidden="true"
        />
        <div
          className="pointer-events-none absolute inset-x-0 top-[14px] bead-band-plum opacity-60"
          aria-hidden="true"
        />

        <motion.div style={{ opacity: heroOpacity }} className="relative z-10 text-center">
          <FadeUp revealed={revealed} delay={0.1} className="mb-8 flex justify-center">
            <MonogramPlaque
              data-testid="hero-monogram"
              className="h-28 w-auto drop-shadow-[0_10px_24px_rgba(74,59,50,0.18)] sm:h-32"
            />
          </FadeUp>
          <FadeUp revealed={revealed} delay={0.2}>
            <p className="text-[10px] uppercase tracking-[0.4em] text-[#5A3A5A] sm:text-xs">
              The Mbele &amp; The Mlambo Family · cordially invite you to
            </p>
          </FadeUp>

          <h1
            data-testid="hero-heading"
            className="mt-8 font-serif-lux font-medium tracking-tight text-[#4A3B32]"
          >
            <MaskedLine index={0} revealed={revealed} className="text-4xl sm:text-5xl lg:text-6xl">
              UMBONDO<span className="gold-foil">/</span>
            </MaskedLine>
            <MaskedLine index={2} revealed={revealed} className="text-4xl sm:text-5xl lg:text-6xl">
              (INGQIBAMASONDO)
            </MaskedLine>
          </h1>

          <MaskedLine
            index={3}
            revealed={revealed}
            className="mt-4 font-serif-lux text-6xl tracking-tight sm:text-7xl"
          >
            <span className="mr-3 align-middle text-2xl italic font-medium text-[#5A3A5A] sm:text-3xl">
              ka
            </span>
            <span className="font-bold text-[#5A3A5A]">MaMdineka</span>
          </MaskedLine>

          <FadeUp revealed={revealed} delay={0.95} className="mt-6">
            <p className="font-serif-lux text-base italic text-[#8C6A53] sm:text-lg">
              Hosted by The Mbele Family for izibulo inkosana{" "}
              <strong className="text-xl font-bold text-[#4A3B32] sm:text-2xl">
                uMfanelo
              </strong>{" "}
              waseMabheleni
            </p>
          </FadeUp>

          <Countdown revealed={revealed} />
        </motion.div>

        <FadeUp
          revealed={revealed}
          delay={1.5}
          className="absolute bottom-8 flex flex-col items-center gap-2"
        >
          <span className="text-xs font-semibold uppercase tracking-[0.35em] text-[#4A3B32]">
            Scroll down
          </span>
          <motion.span
            className="block h-10 w-px bg-[#C5A059]"
            animate={{ scaleY: [0.3, 1, 0.3] }}
            transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
            style={{ transformOrigin: "top" }}
          />
        </FadeUp>
      </section>

      <Marquee />

      {/* 01 — THE CELEBRATION */}
      <Chapter number="01" title="The Celebration">
        <p>Please join us as we celebrate a Zulu cultural milestone.</p>
        <p>
          uMaMdineka, together with the Mlambo family, will visit the Mbele
          family(AmaBhele) bearing uMbondo (Ingqibamasondo) - traditional gifts
          presented as an expression of gratitude, appreciation, and respect to
          her future family for bringing izinkomo(cows) to the Mlambo
          homestead.
        </p>
        <p>
          This is not a wedding yet, but it is indeed a cherished cultural
          ceremony symbolising the strengthening of the bond between the
          Mlambo and Mbele families before the marriage celebrations to show
          gratitude for the Lobola.
        </p>
        <div className="flex items-end justify-between pt-6 text-[#C5A059]/50">
          <Calabash className="h-24 w-20" />
          <Leaves className="h-24 w-20" />
        </div>
      </Chapter>

      {/* 02 — DATE & VENUE */}
      <Chapter number="02" title="Date & Venue" dark>
        <div className="grid gap-10 sm:grid-cols-2">
          <div>
            <Calendar className="mb-3 text-[#C5A059]" size={22} />
            <p className="text-[10px] uppercase tracking-[0.35em] text-[#C5A059]">
              The Date
            </p>
            <p className="mt-2 font-serif-lux text-2xl font-medium text-[#FAF8F5] sm:text-3xl">
              26 September 2026
            </p>
            <p className="mt-2 font-serif-lux text-3xl font-semibold tracking-tight text-[#E8D8B0] sm:text-4xl">
              Time : 10:30
            </p>
          </div>
          <div>
            <MapPin className="mb-3 text-[#C5A059]" size={22} />
            <p className="text-[10px] uppercase tracking-[0.35em] text-[#C5A059]">
              The Venue
            </p>
            <a
              data-testid="venue-map-link"
              href={MAPS_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="group mt-2 block"
            >
              <span className="font-serif-lux text-2xl font-medium leading-snug text-[#FAF8F5] underline decoration-[#C5A059]/50 underline-offset-8 transition-colors group-hover:text-[#E8D8B0] sm:text-3xl">
                230 Cilliers Street,
                <br />
                Mullerstuine, Vanderbijlpark
              </span>
              <span className="mt-3 block text-[10px] uppercase tracking-[0.3em] text-[#C5A059]">
                Tap for directions
              </span>
            </a>
          </div>
        </div>
        <div className="pt-10">
          <a
            data-testid="add-to-calendar-button"
            href="/umbondo.ics"
            download="umbondo-ingqibamasondo.ics"
            className="inline-flex items-center gap-3 rounded-full border border-[#C5A059]/60 px-8 py-4 text-xs uppercase tracking-[0.25em] text-[#E8D8B0] transition-transform duration-300 hover:scale-[1.04] active:scale-95"
          >
            <CalendarPlus size={16} />
            Add to Calendar
          </a>
        </div>
        <div className="pt-8 text-[#E8D8B0]/30">
          <Shield className="h-28 w-20" />
        </div>
      </Chapter>

      {/* 03 — GIFTS */}
      <Chapter number="03" title="Gifts">
        <div className="flex items-start gap-5">
          <GiftBox className="mt-1 h-14 w-14 shrink-0 text-[#C5A059]" />
          <div>
            <p className="font-serif-lux text-xl font-medium text-[#4A3B32] sm:text-2xl">
              Please bring Hard Cash for Games/iNdishi
            </p>
            <p className="mt-2">Gifts and Gift Cards are accepted.</p>
          </div>
        </div>
        <div className="flex justify-end pt-4 text-[#C5A059]/50">
          <Basket className="h-24 w-24" />
        </div>
      </Chapter>

      {/* 04 — DRESS CODE */}
      <Chapter number="04" title="Dress Code" plum>
        <p className="font-serif-lux text-2xl font-medium leading-snug text-[#FAF8F5] sm:text-3xl">
          Traditional African attire of your choice.
        </p>
        <p className="font-serif-lux text-lg italic leading-snug text-[#E8D8B0] sm:text-xl">
          Note: You may add a touch of Purple if you are coming from
          MaMdineka&apos;s side
        </p>
        <div className="pt-6 text-[#E8D8B0]/50">
          <ZuluAttire className="h-32 w-24" />
        </div>
      </Chapter>

      {/* 05 — RSVP */}
      <Chapter number="05" title="RSVP">
        <p>Kindly RSVP before 30 August 2026.</p>
        <p className="font-serif-lux text-lg italic text-[#5A3A5A]">
          Choose only one below
        </p>
        <div className="grid gap-5 pt-4 sm:grid-cols-2">
          {RSVP_CONTACTS.map((c) => (
            <a
              key={c.testid}
              data-testid={c.testid}
              href={c.url}
              target="_blank"
              rel="noopener noreferrer"
              onClick={markRsvpd}
              className="rounded-2xl border border-[#C5A059]/40 bg-white/60 px-6 py-6 text-left shadow-[0_8px_32px_rgba(74,59,50,0.08)] transition-transform duration-300 hover:scale-[1.02]"
            >
              <p className="text-[10px] uppercase tracking-[0.3em] text-[#5A3A5A]">
                {c.label}
              </p>
              <p className="mt-2 font-serif-lux text-3xl font-medium tracking-tight text-[#4A3B32]">
                {c.display}
              </p>
              <span className="mt-4 inline-flex items-center gap-2 rounded-full bg-[#4A3B32] px-5 py-2.5 text-[10px] uppercase tracking-[0.25em] text-[#FAF8F5]">
                <MessageCircle size={14} /> WhatsApp
              </span>
            </a>
          ))}
        </div>
        {hasRsvpd && (
          <p
            data-testid="rsvp-already-message"
            className="pt-6 font-serif-lux text-xl italic text-[#5A3A5A]"
          >
            Siyabonga! It seems you have started with the RSVP process — we
            look forward to celebrating with you. If you are not done with the
            RSVP, you are welcome to continue by clicking above.
          </p>
        )}
      </Chapter>

      <div className="bg-[#FAF8F5] px-6 pb-2 pt-16">
        <ErasedHoof />
      </div>

      <Gallery />

      {/* FOOTER */}
      <footer className="relative bg-[#1A1A1A] px-6 pb-16 pt-0 text-center text-[#FAF8F5]">
        <div className="bead-band mb-1 opacity-80" aria-hidden="true" />
        <div className="bead-band-plum mb-14 opacity-80" aria-hidden="true" />
        <motion.div
          initial={{ opacity: 0, y: 32 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
          className="mx-auto max-w-2xl"
        >
          <p className="text-sm leading-relaxed text-[#FAF8F5]/75 sm:text-base">
            We look forward to celebrating this special cultural occasion with
            family and friends as two families continue their journey towards
            becoming one.
          </p>
          <p
            data-testid="footer-quote"
            className="font-script mt-10 text-3xl leading-snug text-[#C5A059] sm:text-4xl"
          >
            “Umndeni wakhiwa ngothando, ngenhlonipho, kanye nenjabulo.”
          </p>
          <div className="mt-12 flex flex-col items-center gap-4">
            <MonogramPlaque data-testid="footer-monogram" className="h-32 w-auto" />
            <p className="text-[10px] uppercase tracking-[0.35em] text-[#FAF8F5]/50">
              The Mbele Family · 26 September 2026
            </p>
            <p
              data-testid="creator-credit"
              className="pt-6 text-[10px] tracking-[0.2em] text-[#FAF8F5]/35"
            >
              website created by Mfanelo P.(0782772370)
            </p>
          </div>
        </motion.div>
      </footer>
    </main>
  );
}
